/* eslint-disable no-nested-ternary */
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { AllDesKeys } from "../utils/helpers/types";
import Table, { Column } from "../components/table/Table";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";

import Button from "../components/button/Button";
import Modal from "../components/modal/Modal";

import ContextMenu, {
  ContextOption,
} from "../components/context-menu/ContextMenu";
import styles from "./migrate-key.module.scss";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import { getDesKeys } from "../utils/helpers/data/des-keys";
import Select, { Option } from "../components/select/Select";

const DesKeys: FunctionComponent = () => {
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [reset, setReset] = useState(false);
  const [dataSource, setDataSource] = useState<AllDesKeys[]>([]);

  const [issuerCode, setIssuerCode] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const [desKeysOnView, setDesKeysOnView] = useState<AllDesKeys | null>(null);

  const { notify } = useContext(LayoutContext);

  const statusOptions: Option[] = [
    {
      label: "ACTIVE",
      value: "ACTIVE",
    },
    {
      label: "EXPIRED",
      value: "EXPIRED",
    },

    {
      label: "EXPIRING",
      value: "EXPIRING",
    },
  ];

  const fetchDesKeys = async (pageNumber = pagination.pageNumber) => {
    setLoading(true);

    const { error, message, data } = await getDesKeys(
      pageNumber,
      pagination.pageSize,
      issuerCode,
      status
    );

    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch all DES Keys: ${message}`);
      return;
    }

    setDataSource(data.records);
    setPagination({ ...pagination, total: data.total, pageNumber });
  };

  const handleViewReport = async () => {
    setLoading(true);

    try {
      setPagination({ ...pagination, pageNumber: 1 });

      await fetchDesKeys(1);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify("error", "An error occurred while fetching all DES Keys.", error);
    }
  };

  const resetFilters = () => {
    setReset(true);

    setIssuerCode("");
    setStatus("");
    setPagination({ ...pagination, pageNumber: 1 });
  };

  useEffect(() => {
    if (reset) {
      fetchDesKeys();
      setReset(false);
    }
  }, [reset, issuerCode, status]);

  const showResetFilter = issuerCode || status;

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  const columns: Column<AllDesKeys>[] = [
    {
      title: "Issuer Code",
      dataIndex: "issuerCode",
      key: "issuerCode",
    },
    {
      title: "Activated",
      dataIndex: "activated",
      key: "activated",
      render: (activated: boolean) => (activated ? "True" : "False"),
    },

    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (date: string | null) =>
        date ? formatDate(date) : <span className="text-gray-400">Null</span>,
    },
    {
      title: "Created Date",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (date: string | null) =>
        date ? formatDate(date) : <span className="text-gray-400">Null</span>,
    },
    {
      title: "Migrated Date",
      dataIndex: "migratedDate",
      key: "migratedDate",
      render: (date: string | null) =>
        date ? formatDate(date) : <span className="text-gray-400">Null</span>,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={`tag smaller ${
            status === "ACTIVE"
              ? "success"
              : status === "EXPIRING"
              ? "expiring"
              : "error"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "",
      render: (_: unknown, migrate: AllDesKeys) => {
        const options: ContextOption[] = [
          {
            label: "View details",
            onClick: () => setDesKeysOnView(migrate),
          },
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      },
    },
  ];

  useEffect(() => {
    fetchDesKeys();
  }, [pagination.pageNumber, pagination.pageSize]);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">All DES Keys</h1>

      <div className={styles.cont}>
        <div className={styles.containerFlex}>
          <div className={styles.container}>
            <div className="input-group ">
              <Input
                value={issuerCode || ""}
                onChange={(value) => setIssuerCode(value as string)}
                placeholder="Enter Issuer Code"
                responsive
              />
            </div>

            <div className="input-group ">
              <Select
                options={statusOptions}
                value={status || ""}
                onSelect={(value) => setStatus(value as string)}
                placeholder="Select Status"
                responsive
              />
            </div>
          </div>
        </div>

        <div className={styles.inputView}>
          <Button
            type="accent"
            size="large"
            className="strong no-pdf-print"
            onClick={handleViewReport}
          >
            Filter
          </Button>
          <span className={styles.button} />
        </div>
      </div>

      {showResetFilter && (
        <Button type="accent" size="small" onClick={resetFilters}>
          RESET
        </Button>
      )}
      <br />

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onPageChange={(pageNumber) =>
          setPagination({ ...pagination, pageNumber })
        }
        onPageSizeChange={(pageSize) =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
      />

      <DesKeyViewModal
        desKey={desKeysOnView}
        cancel={() => setDesKeysOnView(null)}
        visible={Boolean(desKeysOnView)}
      />
    </main>
  );
};

interface desKeysModalProps {
  desKey: AllDesKeys | null;
  cancel: () => void;
  visible: boolean;
}

const DesKeyViewModal: FunctionComponent<desKeysModalProps> = ({
  desKey: _desKey,
  cancel,
  visible,
}) => {
  const [loading] = useState(false);
  const [desKey, setDesKey] = useState<AllDesKeys | null>(_desKey);

  useEffect(() => {
    setDesKey(_desKey);
  }, [_desKey]);

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{desKey?.id || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created Date:</span>
            <span className={styles.value}>
              {desKey?.createdOn ? formatDate(desKey.createdOn) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Updated Date:</span>
            <span className={styles.value}>
              {desKey?.lastUpdatedOn ? formatDate(desKey.lastUpdatedOn) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Issuer Code:</span>
            <span className={styles.value}>{desKey?.issuerCode || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Expiry Date:</span>
            <span className={styles.value}>
              {" "}
              {desKey?.expiryDate ? formatDate(desKey.expiryDate) : "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Activated:</span>
            <span className={styles.value}>
              {desKey?.activated != null
                ? desKey.activated
                  ? "True"
                  : "False"
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Label:</span>
            <span className={styles.value}>{desKey?.keyLabel || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Env Flag:</span>
            <span className={styles.value}>{desKey?.envFlag || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Activation Date:</span>
            <span className={styles.value}>
              {" "}
              {desKey?.activationDate
                ? formatDate(desKey.activationDate)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Description:</span>
            <span className={styles.value}>{desKey?.description || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Index:</span>
            <span className={styles.value}>{desKey?.keyIndex || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Version:</span>
            <span className={styles.value}>{desKey?.keyVersion || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Migrated Date:</span>
            <span className={styles.value}>
              {" "}
              {desKey?.migratedDate ? formatDate(desKey.migratedDate) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Status:</span>
            <span className={styles.value}>{desKey?.status || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Des Key Infold:</span>
            <span className={styles.value}>
              {desKey?.desKeyInfoId || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Type:</span>
            <span className={styles.value}>{desKey?.keyType || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Category:</span>
            <span className={styles.value}>{desKey?.keyCategory || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Type Code:</span>
            <span className={styles.value}>{desKey?.keyTypeCode || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Parent Key:</span>
            <span className={styles.value}>{desKey?.parentKey || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>KeyValue under ZMK:</span>
            <span className={styles.value}>
              {desKey?.keyValueUnderZmk || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>KeyValue under LMK:</span>
            <span className={styles.value}>
              {desKey?.keyValueUnderLmk || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>KeyValue under Old LMK:</span>
            <span className={styles.value}>
              {desKey?.keyValueUnderOldLmk || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Length:</span>
            <span className={styles.value}>{desKey?.keyLength || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>KCV:</span>
            <span className={styles.value}>{desKey?.kcv || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Encryption Mode:</span>
            <span className={styles.value}>
              {desKey?.encryptionMode || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Auditable Name:</span>
            <span className={styles.value}>
              {desKey?.auditableName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Auditable Id:</span>
            <span className={styles.value}>{desKey?.auditableId || "N/A"}</span>
          </div>
        </section>
      )}
    </Modal>
  );
};
export default DesKeys;
